import type { TypingClientEvent } from '@tandemone/types'
import { useCallback, useEffect, useRef } from 'react'

import { getSocket } from '@/api/socket'

const STOP_DELAY_MS = 3_000

/** throttled typing emitter for the composer. on each keystroke call
 * `notifyTyping()` . the first call after idle emits `isTyping: true`;
 * subsequent calls within 3s just keep the timer alive. when 3s elapse
 * without input the hook auto-emits `isTyping: false`. callers also call
 * `notifyStopped()` explicitly on send to clear the indicator
 * immediately.
 *
 * the conversationId is passed in once and captured by ref so callers
 * don't need to thread it into every notify call.
 */
export function useTypingEmitter(conversationId: string | undefined): {
  notifyTyping: () => void
  notifyStopped: () => void
} {
  const isTypingRef = useRef(false)
  const stopTimerRef = useRef<number | null>(null)

  /** emit takes the conversation id explicitly. the timer callback +
   * cleanup capture the id at schedule time so navigation can't deliver
   * a stop event to the wrong conversation room (which would otherwise
   * leave the indicator stuck on the conversation we just left).
   */
  function emit(targetConversationId: string, isTyping: boolean): void {
    if (!targetConversationId) return
    const payload: TypingClientEvent = {
      conversationId: targetConversationId,
      isTyping
    }
    getSocket().emit('client:typing', payload)
  }

  const clearStopTimer = useCallback(() => {
    if (stopTimerRef.current !== null) {
      window.clearTimeout(stopTimerRef.current)
      stopTimerRef.current = null
    }
  }, [])

  const notifyStopped = useCallback((): void => {
    clearStopTimer()
    if (isTypingRef.current && conversationId) {
      isTypingRef.current = false
      emit(conversationId, false)
    }
  }, [clearStopTimer, conversationId])

  const notifyTyping = useCallback((): void => {
    if (!conversationId) return
    if (!isTypingRef.current) {
      isTypingRef.current = true
      emit(conversationId, true)
    }
    clearStopTimer()
    stopTimerRef.current = window.setTimeout(() => {
      isTypingRef.current = false
      emit(conversationId, false)
      stopTimerRef.current = null
    }, STOP_DELAY_MS)
  }, [clearStopTimer, conversationId])

  /** on unmount or conversation change, send a final stop pinned to the
   * leaving id so the indicator falls off the other participants' UI
   * immediately. capturing conversationId here (and depending on it)
   * means the cleanup closure always references the conversation we're
   * leaving, not the new one.
   */
  useEffect(() => {
    const leavingId = conversationId
    return () => {
      clearStopTimer()
      if (isTypingRef.current && leavingId) {
        isTypingRef.current = false
        emit(leavingId, false)
      }
    }
  }, [clearStopTimer, conversationId])

  return { notifyTyping, notifyStopped }
}
